import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ClockInOut.css';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://healthcare.visionkraftconsulting.com/api'
  : 'http://localhost:3025/api';

function ClockInOut() {
  const [status, setStatus] = useState('ready'); // ready, clocked-in, clocked-out
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentSession, setCurrentSession] = useState(null);
  const [clockHistory, setClockHistory] = useState([]);
  const [location, setLocation] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showHistory, setShowHistory] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [allowNoLocation, setAllowNoLocation] = useState(false);
  const [requestingLocation, setRequestingLocation] = useState(false);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch clock history on component mount
  useEffect(() => {
    fetchClockHistory();
    checkActiveSession();
  }, []);

  // Check if there's an active clock-in session
  const checkActiveSession = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) return;
      
      const response = await axios.get(`${API_BASE_URL}/clock/active-session`, {
        withCredentials: true
      });
      
      if (response.data.success && response.data.session) {
        setCurrentSession(response.data.session);
        setStatus('clocked-in');
      }
    } catch (err) {
      console.log('No active session found');
    }
  };

  // Fetch clock history
  const fetchClockHistory = async () => {
    try {
      setLoadingHistory(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) return;
      
      const response = await axios.get(`${API_BASE_URL}/clock/logs/${user.id}`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setClockHistory(response.data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch clock history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Get user's location
  const getLocation = async () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      const options = {
        enableHighAccuracy: false, // Set to false for better mobile compatibility
        timeout: 30000, // Increased timeout for mobile
        maximumAge: 0
      };

      // Show requesting location state
      setRequestingLocation(true);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setRequestingLocation(false);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          setRequestingLocation(false);
          let errorMessage = 'Unable to get location';
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please tap "Allow" when prompted for location access, or check your browser/device settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please ensure GPS/Location Services are enabled on your device.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please ensure you have a stable connection and try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting location.';
          }
          console.error('Geolocation error:', error);
          reject(new Error(errorMessage));
        },
        options
      );
    });
  };

  // Handle Clock In
  const handleClockIn = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setLocationError(false);

    let userLocation = null;
    
    try {
      // Try to get location
      if (!allowNoLocation) {
        try {
          userLocation = await getLocation();
          setLocation(userLocation);
        } catch (locationErr) {
          setLocationError(true);
          setError(locationErr.message);
          setLoading(false);
          return;
        }
      }

      // Check authentication
      const isAuthenticated = localStorage.getItem('isAuthenticated');
      if (!isAuthenticated || isAuthenticated !== 'true') {
        throw new Error('Please log in to continue.');
      }

      // Send clock-in request
      const response = await axios.post(
        `${API_BASE_URL}/clock/clock-in`,
        {
          latitude: userLocation?.latitude || 0,
          longitude: userLocation?.longitude || 0,
        },
        {
          withCredentials: true
        }
      );

      if (response.data.success) {
        setStatus('clocked-in');
        setCurrentSession({
          id: response.data.clockInId,
          clock_in_time: new Date().toISOString(),
          latitude: userLocation?.latitude || 0,
          longitude: userLocation?.longitude || 0
        });
        setSuccess('Successfully clocked in!');
        fetchClockHistory(); // Refresh history
      }
    } catch (err) {
      console.error('Clock-In failed:', err);
      setError(err.response?.data?.message || err.message || 'Clock-In Failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle Clock Out
  const handleClockOut = async () => {
    if (!currentSession) {
      setError('No active clock-in session found.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/clock/clock-out`,
        {
          clockInId: currentSession.id,
        },
        {
          withCredentials: true
        }
      );

      if (response.data.success) {
        setStatus('clocked-out');
        setCurrentSession(null);
        setLocation(null);
        setSuccess('Successfully clocked out!');
        fetchClockHistory(); // Refresh history
      }
    } catch (err) {
      console.error('Clock-Out failed:', err);
      setError(err.response?.data?.message || 'Clock-Out Failed');
    } finally {
      setLoading(false);
    }
  };

  // Format time
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate duration
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Ongoing';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diff = end - start;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  // Calculate elapsed time for current session
  const getElapsedTime = () => {
    if (!currentSession) return '00:00:00';
    
    const start = new Date(currentSession.clock_in_time);
    const now = new Date();
    const diff = now - start;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="clock-container">
      <div className="clock-header">
        <h1>Time Clock</h1>
        <div className="current-time">
          <div className="time-display">{formatTime(currentTime)}</div>
          <div className="date-display">{currentTime.toDateString()}</div>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-circle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
          {locationError && !allowNoLocation && (
            <div className="mt-3">
              <div className="d-flex flex-column gap-2">
                <small className="text-muted">
                  <strong>For mobile users:</strong> Please tap "Allow" when your browser asks for location permission.
                </small>
                <button 
                  className="btn btn-warning btn-sm"
                  onClick={() => {
                    setAllowNoLocation(true);
                    setError('');
                    setLocationError(false);
                  }}
                >
                  <i className="bi bi-geo me-2"></i>
                  Continue without location
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle me-2"></i>
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* Location Request Notice */}
      {requestingLocation && (
        <div className="alert alert-info" role="alert">
          <div className="d-flex align-items-center">
            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            <div>
              <strong>Requesting location access...</strong>
              <br />
              <small>Please tap "Allow" when prompted to share your location.</small>
            </div>
          </div>
        </div>
      )}

      {/* Current Session Card */}
      <div className="status-card">
        <div className="status-header">
          <h3>Current Status</h3>
          <span className={`status-badge ${status}`}>
            {status === 'clocked-in' ? (
              <>
                <i className="bi bi-circle-fill text-success me-2"></i>
                Clocked In
              </>
            ) : (
              <>
                <i className="bi bi-circle text-secondary me-2"></i>
                Not Clocked In
              </>
            )}
          </span>
        </div>
        
        {currentSession && (
          <div className="session-info">
            <div className="info-row">
              <span className="info-label">Clock In Time:</span>
              <span className="info-value">{formatDate(currentSession.clock_in_time)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Duration:</span>
              <span className="info-value elapsed-time">{getElapsedTime()}</span>
            </div>
            {currentSession && currentSession.latitude && currentSession.longitude && (
              <div className="info-row">
                <span className="info-label">Location:</span>
                <span className="info-value">
                  <i className="bi bi-geo-alt me-1"></i>
                  {parseFloat(currentSession.latitude).toFixed(4)}, {parseFloat(currentSession.longitude).toFixed(4)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Clock In/Out Buttons */}
        <div className="clock-actions">
          {status !== 'clocked-in' ? (
            <button
              className="btn btn-primary btn-lg clock-btn"
              onClick={handleClockIn}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Clocking In...
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  Clock In
                </>
              )}
            </button>
          ) : (
            <button
              className="btn btn-danger btn-lg clock-btn"
              onClick={handleClockOut}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Clocking Out...
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-right me-2"></i>
                  Clock Out
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* History Section */}
      <div className="history-section">
        <div className="history-header">
          <h3>
            <i className="bi bi-clock-history me-2"></i>
            Clock History
          </h3>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? 'Hide' : 'Show'} History
          </button>
        </div>

        {showHistory && (
          <div className="history-content">
            {loadingHistory ? (
              <div className="text-center p-4">
                <span className="spinner-border spinner-border-sm" role="status"></span>
                <span className="ms-2">Loading history...</span>
              </div>
            ) : clockHistory.length === 0 ? (
              <div className="text-center text-muted p-4">
                <i className="bi bi-inbox fs-1"></i>
                <p className="mt-2">No clock history found</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Clock In</th>
                      <th>Clock Out</th>
                      <th>Duration</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clockHistory.map((entry) => (
                      <tr key={entry.id}>
                        <td>{new Date(entry.clock_in_time).toLocaleDateString()}</td>
                        <td>{new Date(entry.clock_in_time).toLocaleTimeString()}</td>
                        <td>
                          {entry.clock_out_time
                            ? new Date(entry.clock_out_time).toLocaleTimeString()
                            : <span className="badge bg-success">Active</span>}
                        </td>
                        <td>{calculateDuration(entry.clock_in_time, entry.clock_out_time)}</td>
                        <td>
                          {entry.latitude && entry.longitude ? (
                            <span className="text-muted small">
                              <i className="bi bi-geo-alt"></i>
                              {parseFloat(entry.latitude).toFixed(2)}, {parseFloat(entry.longitude).toFixed(2)}
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClockInOut;